import {
  RETURN_DISPOSAL_REQUEST_MESSAGE,
  RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH,
  RTS_DIRECT_CONTACT_NAME_MAX_LENGTH,
  RTS_DIRECT_CONTACT_PHONE_PATTERN,
} from '~/constants';

import type { TypeRtsDirectRentalCard } from '~/types/receive.types';

/** 담당자 연락처 형식 충족 여부 — 숫자·하이픈만 9~15자 */
export const isRtsDirectContactPhoneValid = (value: string): boolean =>
  RTS_DIRECT_CONTACT_PHONE_PATTERN.test(value.trim());

/** 렌탈사 카드 필수값 충족 여부 — 다섯 항목 모두 필수 */
export const isRtsDirectRentalCardComplete = (card: TypeRtsDirectRentalCard): boolean =>
  !!card.rentalCompany &&
  card.businessCode.trim() !== '' &&
  card.contactName.trim() !== '' &&
  card.contactPhone.trim() !== '' &&
  card.releaseAddress.trim() !== '';

/**
 * 렌탈사 카드 신청 가능 여부 — 필수값에 더해 업체코드·담당자 성함 길이 상한과 연락처 형식까지 본다.
 *
 * @note 입력 컴포넌트가 길이·문자를 1차로 막지만, 한글 IME 조합 경계처럼 새는 값이 있어도 신청은 막히도록 값 기준으로 다시 판정한다.
 */
export const isRtsDirectRentalCardValid = (card: TypeRtsDirectRentalCard): boolean =>
  isRtsDirectRentalCardComplete(card) &&
  card.businessCode.trim().length <= RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH &&
  card.contactName.trim().length <= RTS_DIRECT_CONTACT_NAME_MAX_LENGTH &&
  isRtsDirectContactPhoneValid(card.contactPhone);

/**
 * 담당자 연락처 인라인 에러 재계산 — 포커스가 벗어난 카드만 판정한다.
 *
 * @note 비어 있거나 형식에 맞지 않으면 에러를 붙이고, 만족하면 지운다. 다른 카드는 아직 입력 전일 수 있어 건드리지 않는다.
 */
export const validateRtsDirectContactPhone = (
  cards: TypeRtsDirectRentalCard[],
  id: string
): TypeRtsDirectRentalCard[] =>
  cards.map((card) => {
    if (card.id !== id) return card;

    return {
      ...card,
      contactPhoneError: isRtsDirectContactPhoneValid(card.contactPhone)
        ? undefined
        : RETURN_DISPOSAL_REQUEST_MESSAGE.CONTACT_PHONE_FORMAT_ERROR,
    };
  });

/** 렌탈사 카드 값 변경 — 연락처를 다시 입력하면 그 카드의 형식 에러는 지우고, 포커스 이탈 시 다시 판정한다 */
export const changeRtsDirectRentalCard = (
  cards: TypeRtsDirectRentalCard[],
  id: string,
  patch: Partial<TypeRtsDirectRentalCard>
): TypeRtsDirectRentalCard[] =>
  cards.map((card) => {
    if (card.id !== id) return card;

    return 'contactPhone' in patch ? { ...card, ...patch, contactPhoneError: undefined } : { ...card, ...patch };
  });
